// EMITIR / RETIFICAR o movimento, num diálogo só.
//
// Até 28/09/2026 o ato inteiro acontecia fora da tela: um `window.prompt`
// pedia o motivo, o botão ficava desabilitado sem dizer por quê, e minutos
// depois (416 s na retificação da ALECE 202609) um `alert()` do navegador
// aparecia sem aviso. O operador não sabia se o sistema estava trabalhando.
//
// Aqui o diálogo tem três estados, e cada um diz o que se sabe:
//   confirmar  só na retificação — o motivo, que a API recusa sem;
//   gerando    há quanto tempo, e que o diálogo não fecha no meio;
//   resultado  o que saiu (ou por que não saiu), no lugar do alert.

import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Modal, TextareaField } from "@atlas/ui/web";
import type { AdminFolha, AtlasClient } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { AndamentoEnvio } from "../../components/AndamentoEnvio";

type Emissao = Awaited<ReturnType<AtlasClient["admin"]["emitirMovimento"]>>;

/** Os mesmos limites da rota — descobrir por um 400 seria pior do que avisar no campo. */
const MOTIVO_MIN = 5;
const MOTIVO_MAX = 300;

/**
 * O QUE A ESCRITA NO LAYOUT DO ÓRGÃO CUSTOU, no mesmo resultado da emissão.
 *
 * Numa unidade no padrão isto é uma linha só dizendo o layout — que é
 * exatamente o que se espera ver, e confirma que nada mudou. Numa unidade
 * adaptada aparecem as duas perdas que importam: dívida somada numa linha só, e
 * a ausência do identificador, que é o que joga o casamento do retorno no
 * caminho por atributos.
 */
export function resumoDaEmissao(r: Pick<Emissao, "layout" | "relatorioGeracao">): string[] {
  const { layout, relatorioGeracao: rel } = r;
  if (!layout) return [];
  const linhas = [`Layout: ${layout.codigo} v${layout.versao}`];
  if (rel) {
    linhas.push(`Linhas no arquivo: ${rel.linhas ?? 0}`);
    linhas.push(`Dívidas instruídas: ${rel.dividas_instruidas ?? 0}`);
    if (Number(rel.dividas_somadas ?? 0) > 0) {
      linhas.push(
        `Dívidas somadas: ${rel.dividas_somadas} em ${rel.grupos_somados} linha(s) — ` +
          "o layout do órgão aceita uma linha por matrícula e verba.",
      );
    }
    linhas.push(
      Number(rel.identificador_escrito ?? 0) === 1
        ? "Identificador da dívida (ADF): escrito no arquivo."
        : "Identificador da dívida (ADF): SEM campo neste layout — o retorno casa por matrícula, verba e valor.",
    );
    if (Number(rel.campos_sem_correspondencia ?? 0) > 0) {
      linhas.push(`Campos do modelo sem lugar neste layout: ${rel.campos_sem_correspondencia}`);
    }
  }
  return linhas;
}

export function EmitirMovimentoModal({
  folha,
  onClose,
  fmtInstante,
}: {
  folha: AdminFolha;
  onClose: () => void;
  fmtInstante: (iso: string | undefined) => string;
}) {
  const qc = useQueryClient();
  const retificacao = !!folha.movimentoEmitido;
  const [motivo, setMotivo] = useState("");
  const [tocado, setTocado] = useState(false);

  // ESTADO PRÓPRIO, e não o de `useMutation`. A primeira emissão dispara na
  // abertura do diálogo, e sob o StrictMode (main.tsx) o desmonte simulado
  // desliga o observador da mutação já em voo: o resultado nunca chegava e a
  // tela ficava em "Gerando" para sempre, mesmo com a API já tendo respondido
  // (28/09/2026, emissão de Joinville no local). O estado do componente
  // sobrevive ao desmonte simulado; o observador não.
  const [fase, setFase] = useState<
    | { tipo: "confirmar" }
    | { tipo: "gerando" }
    | { tipo: "ok"; r: Emissao }
    | { tipo: "erro"; mensagem: string }
  >({ tipo: retificacao ? "confirmar" : "gerando" });

  // TRAVA SÍNCRONA: um `disabled` só vale depois que a tela redesenha, e dois
  // cliques no mesmo instante passariam os dois. Solta quando a emissão termina,
  // dê certo ou dê erro — e aí o "Tentar novamente" pode disparar.
  const emitindo = useRef(false);
  const disparar = async () => {
    if (emitindo.current) return;
    emitindo.current = true;
    setFase({ tipo: "gerando" });
    try {
      const r = await atlas.admin.emitirMovimento(folha.id, retificacao ? motivo.trim() : undefined);
      void qc.invalidateQueries({ queryKey: ["admin", "folhas"] });
      setFase({ tipo: "ok", r });
    } catch (e) {
      setFase({ tipo: "erro", mensagem: e instanceof Error ? e.message : String(e) });
    } finally {
      emitindo.current = false;
    }
  };

  // A PRIMEIRA EMISSÃO não pergunta nada — era assim antes, e não há motivo a
  // pedir. O diálogo abre direto no andamento. O ref também segura o segundo
  // disparo do StrictMode em dev.
  useEffect(() => {
    if (!retificacao) void disparar();
    // Só na abertura.
  }, []);

  const motivoLimpo = motivo.trim();
  const erroMotivo =
    motivoLimpo.length < MOTIVO_MIN
      ? "Escreva o motivo — ele fica no histórico ao lado das duas versões."
      : motivoLimpo.length > MOTIVO_MAX
        ? `No máximo ${MOTIVO_MAX} caracteres.`
        : null;

  const gerando = fase.tipo === "gerando";
  // Fechar no meio não cancela nada no servidor, só esconderia o andamento — e
  // era justamente a falta dele que fazia o operador achar que travou.
  const fechar = () => { if (!gerando) onClose(); };

  const nome = `${folha.prefeitura} · ${folha.competencia}`;

  if (gerando) {
    return (
      <Modal
        open
        onClose={fechar}
        eyebrow={retificacao ? "Retificação do movimento" : "Emissão do movimento"}
        title={retificacao ? "Retificando o movimento" : "Emitindo o movimento"}
        subtitle={nome}
      >
        <style>{`@keyframes atlas-spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          <div
            aria-hidden
            style={{
              flexShrink: 0, width: 28, height: 28, borderRadius: "50%", marginTop: 8,
              border: "3px solid color-mix(in srgb, var(--accent) 20%, transparent)",
              borderTopColor: "var(--accent)",
              animation: "atlas-spin 900ms linear infinite",
            }}
          />
          <AndamentoEnvio
            ativo
            verbo={`Gerando o movimento de ${nome}`}
            expectativa="O arquivo é montado e congelado de uma vez; em folhas grandes isso pode levar alguns minutos."
            consequencia="Não feche esta página: o resultado aparece aqui quando a emissão terminar."
          />
        </div>
      </Modal>
    );
  }

  if (fase.tipo === "ok") {
    const r = fase.r;
    const resumo = resumoDaEmissao(r);
    // `avisos` chega com a politica do orgao sem o arquivo da consignataria
    // (#202): a emissao segue, mas quem emite precisa saber o que saiu assim.
    // Lido de forma opcional porque o SDK desta base ainda nao declara o campo —
    // quando a #202 entrar, isto ja' mostra, e o alert que fazia isso nao volta.
    const avisos = (r as Emissao & { avisos?: string[] }).avisos ?? [];
    return (
      <Modal
        open
        onClose={onClose}
        eyebrow={r.retificacao ? "Retificação do movimento" : "Emissão do movimento"}
        title={r.retificacao ? "Movimento retificado" : "Movimento emitido"}
        subtitle={nome}
        footer={<Button onClick={onClose}>Fechar</Button>}
      >
        <p role="status" style={{ margin: 0, fontSize: 14 }}>
          {r.retificacao
            ? "O órgão passa a baixar a nova versão; a anterior fica no histórico."
            : "O movimento foi congelado. O órgão já pode baixar."}
        </p>
        {resumo.length > 0 ? (
          <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 4, fontSize: 13 }}>
            {resumo.map((l) => <li key={l}>{l}</li>)}
          </ul>
        ) : null}
        {avisos.length > 0 ? (
          <div role="alert" style={{ display: "grid", gap: 4, fontSize: 13, color: "var(--gold-500)" }}>
            <b>Atenção</b>
            <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 4 }}>
              {avisos.map((a) => <li key={a}>{a}</li>)}
            </ul>
          </div>
        ) : null}
        <p style={{ margin: 0, fontSize: 12, color: "var(--text-muted)" }}>
          O relatório completo fica em Troca de arquivos, ao lado do registro.
        </p>
      </Modal>
    );
  }

  if (fase.tipo === "erro") {
    const msg = fase.mensagem;
    return (
      <Modal
        open
        onClose={onClose}
        danger
        eyebrow={retificacao ? "Retificação do movimento" : "Emissão do movimento"}
        title={retificacao ? "O movimento não foi retificado" : "O movimento não foi emitido"}
        subtitle={nome}
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>Fechar</Button>
            {/* Na retificação volta ao motivo (preservado) em vez de disparar
                direto: o erro pode ser justamente algo a corrigir antes. */}
            <Button onClick={() => (retificacao ? setFase({ tipo: "confirmar" }) : void disparar())}>Tentar novamente</Button>
          </>
        }
      >
        <p role="alert" style={{ margin: 0, fontSize: 14, whiteSpace: "pre-wrap" }}>{msg}</p>
        <p style={{ margin: 0, fontSize: 12, color: "var(--text-muted)" }}>
          Nada foi entregue ao órgão por esta tentativa.
        </p>
      </Modal>
    );
  }

  // Confirmar — só a retificação chega aqui.
  const m = folha.movimentoEmitido;
  return (
    <Modal
      open
      onClose={fechar}
      showClose
      eyebrow="Retificação do movimento"
      title={`Retificar o movimento de ${folha.competencia}`}
      subtitle={folha.prefeitura}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button
            disabled={!!erroMotivo}
            onClick={() => { setTocado(true); if (!erroMotivo) void disparar(); }}
          >
            ↻ Retificar
          </Button>
        </>
      }
    >
      {m ? (
        <p style={{ margin: 0, fontSize: 14 }}>
          Emitido em <b>{fmtInstante(m.em)}</b> por <b>{m.porNome}</b>.
        </p>
      ) : null}
      <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
        Reemitir é uma <b>retificação</b>: as duas versões ficam no histórico e o órgão precisa saber qual vale.
      </p>
      <TextareaField
        id="retificacao-motivo"
        label="Por que está retificando?"
        required
        maxLength={MOTIVO_MAX}
        value={motivo}
        error={tocado ? erroMotivo ?? undefined : undefined}
        hint={`Fica no histórico ao lado das duas versões. De ${MOTIVO_MIN} a ${MOTIVO_MAX} caracteres.`}
        onChange={(e) => setMotivo(e.target.value)}
        onBlur={() => setTocado(true)}
      />
    </Modal>
  );
}
